import React, { useState, useEffect } from "react";
import {
  Grid,
  TextField,
  Button,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import styles from "./ContactDetails.module.css";
import Logo from "../components/Logo";
import SideBackground from "../components/SideBackground";
import api from "../API/axios";
import NotificationSnackbar from "../components/NotificationSnackbar";
import useNotification from "../hooks/useNotification";

export default function TwoFactorApp() {
  const { notification, showNotification, hideNotification } =
    useNotification();
  const [form, setForm] = useState({
    method: "app",
    verificationCode: "",
  });
  const [errors, setErrors] = useState({
    verificationCode: "",
  });
  const [qrCodeUrl, setQrCodeUrl] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Generate QR code when component mounts
    generateQRCode();
  }, []);

  const generateQRCode = async () => {
    try {
      const params = new URLSearchParams(window.location.search);
      const verificationToken = params.get("token");

      if (!verificationToken) {
        showNotification("Verification token missing.", "error");
        return;
      }

      const response = await api.post("/TwoFactor/setup-app", {
        token: verificationToken,
      });

      // Access QR code URL from nested data structure
      const qrUrl =
        response.data?.data?.qrCodeUrl || response.data?.qrCodeUrl || "";
      setQrCodeUrl(qrUrl);
      setLoading(false);
    } catch (err) {
      console.error(err);
      showNotification(
        "Failed to generate QR code. Please try again.",
        "error"
      );
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });

    // Clear errors when user starts typing
    if (errors[name]) {
      setErrors({ ...errors, [name]: "" });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Reset errors
    setErrors({ verificationCode: "" });

    // Validate verification code
    if (!form.verificationCode || form.verificationCode.length !== 6) {
     showNotification(
       "Please enter a valid 6-digit verification code.",
       "error"
     );
      return;
    }

    // Get verification token from URL
    const params = new URLSearchParams(window.location.search);
    const verificationToken = params.get("token");
    if (!verificationToken) {
      showNotification("Verification token missing.", "error");
      return;
    }

    try {
      await api.post("/TwoFactor/verify-app", {
        token: verificationToken,
        verificationCode: form.verificationCode,
      });

      // Redirect to bypass codes page
      window.location.href = `/bypass-codes?token=${verificationToken}`;
    } catch (err) {
      console.error(err);
      showNotification(
        "The verification code is incorrect or has expired.",
        "error"
      );
    }
  };

  return (
    <div className={styles.contactDetailsRoot}>
      <div className={styles.contactDetailsLeft}>
        <div className={styles.contactDetailsLogoWrap}>
          <Logo />
        </div>
        <form
          className={styles.contactDetailsForm}
          onSubmit={handleSubmit}
          style={{ marginTop: "80px" }}
        >
          <h2>Complete Setup</h2>
          <h4
            style={{
              textAlign: "center",
              fontWeight: 600,
              margin: "0px",
              marginTop: "-20px",
              color: "#888",
            }}
          >
            2 Factor Method
          </h4>
          <div
            style={{
              fontSize: "13px",
              width: "100%",
              textAlign: "center",
              color: "#666",
            }}
          >
            <p>
              To help keep your account secure, please select your preferred
              method of
              <br />
              two factor authentication (2FA).
            </p>
          </div>
          <div
            style={{
              width: "100%",
              maxWidth: 260,
              display: "flex",
              flexDirection: "column",
              margin: "0 auto",
            }}
          >
            <TextField
              select
              name="method"
              value={form.method}
              label="2 Factor Method"
              disabled
              fullWidth
              sx={{
                maxWidth: 260,
                marginBottom: 1,
              }}
            >
              <MenuItem value="app">Authenticator App</MenuItem>
            </TextField>
          </div>

          <div
            style={{
              fontSize: "13px",
              width: "100%",
              textAlign: "center",
              color: "#666",
            }}
          >
            <p>
              {" "}
              <strong>Step 1) </strong>
              Open or download your chosen authentication app
              <br />
              and scan the below QR code
            </p>
          </div>
          {/* QR Code Display */}
          <div
            style={{
              marginBottom: 4,
              width: "100%",
              display: "flex",
              justifyContent: "center",
            }}
          >
            {loading ? (
              <div style={{ padding: "20px", color: "#666" }}>
                Generating QR Code...
              </div>
            ) : qrCodeUrl ? (
              <div>
                <img
                  src={qrCodeUrl}
                  alt="QR Code for 2FA setup"
                  style={{
                    maxWidth: "120px",
                    border: "1px solid #ddd",
                    padding: "10px",
                  }}
                />
              </div>
            ) : (
              <div style={{ color: "red" }}>Failed to generate QR code</div>
            )}
          </div>

          <div
            style={{
              fontSize: "13px",
              width: "100%",
              textAlign: "center",
              color: "#666",
            }}
          >
            <p>
              {" "}
              <strong>Step 2) </strong>
              Enter the verification code from your authenticator
              <br />
              app below
            </p>
          </div>

          <div
            style={{
              width: "100%",
              maxWidth: 260,
              display: "flex",
              flexDirection: "column",
              margin: "0 auto",
            }}
          >
            <TextField
              type="text"
              label="Verification Code"
              name="verificationCode"
              value={form.verificationCode}
              onChange={(e) =>
                setForm({
                  ...form,
                  verificationCode: e.target.value.replace(/[^0-9]/g, ""),
                })
              }
              autoFocus
              fullWidth
              required
              error={!!errors.verificationCode}
              inputProps={{ maxlength: 6, pattern: "[0-9]*" }}
            />
            <Button
              type="submit"
              variant="contained"
              size="large"
              disabled={loading}
              sx={{
                mt: 2,
                width: "100%",
                backgroundColor: "var(--color-primary)",
                "&:hover": { backgroundColor: "var(--color-secondary)" },
              }}
            >
              Continue
            </Button>
          </div>

          {/* Verification code error */}
          {/* {errors.verificationCode && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {errors.verificationCode}
            </Alert>
          )} */}
        </form>
      </div>
      <SideBackground>
        <div className={styles.contactDetailsBgOverlay}></div>
      </SideBackground>

      {/* Notification Snackbar */}
      <NotificationSnackbar
        open={notification.open}
        message={notification.message}
        severity={notification.severity}
        onClose={hideNotification}
      />
    </div>
  );
}
